import type { VerticalPack } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { PACKS } from '@/app/(platform)/(subscription-gated)/labs/_packs/registry';

import { EXPLORE_PACK } from '../_packs/explore';

/**
 * The packs the PUBLIC PREVIEW can render: the three product verticals, plus Career Explorer.
 *
 * 🔴 WHY A SECOND REGISTRY EXISTS AT ALL. Career Explorer has to be reachable at `/try/explore` and
 * unreachable everywhere else. The shared `_packs/registry.ts` cannot express that, because three live
 * surfaces enumerate or index it:
 *
 *   - `/labs/[vertical]`, the gated sprint route, resolves any registered slug;
 *   - `use-labs-entry.ts` walks registered packs to decide which entry modal wins when a learner's
 *     declared interests match more than one vertical;
 *   - the side-nav rail reads packs to build its sprint items.
 *
 * All three are surfaces Experiment 3 is currently being measured on. Adding a fourth pack there would
 * change which modal some learners see and which rail items render, mid-experiment, for a pack that has
 * no business inside the product. The cost of a second registry is this file; the cost of one registry
 * is a live experiment quietly changing shape.
 *
 * So the direction of the dependency matters: this module reads `PACKS`, and nothing in `PACKS` reads
 * this. A pack added to the product registry appears here automatically. A pack added here can never
 * leak the other way.
 */
export const PREVIEW_PACKS = {
  ...PACKS,
  explore: EXPLORE_PACK,
} as const satisfies Readonly<Record<string, VerticalPack>>;

/** Slugs the preview route can render, i.e. the values `generateStaticParams` emits. */
export type PreviewVerticalSlug = keyof typeof PREVIEW_PACKS;

/** Type guard for an untrusted route segment. */
export function isPreviewVertical(slug: string): slug is PreviewVerticalSlug {
  return Object.hasOwn(PREVIEW_PACKS, slug);
}

/**
 * Resolve a `/try/[vertical]` segment to its pack, or null for an unknown slug.
 *
 * Deliberately NOT `resolvePack` from the product registry. That function is right for `/labs`, and
 * using it here would 404 `/try/explore` while quietly suggesting the two routes serve the same set.
 */
export function resolvePreviewPack(slug: string): VerticalPack | null {
  return isPreviewVertical(slug) ? PREVIEW_PACKS[slug] : null;
}
